import React, { useState, useEffect, useCallback } from "react";
import { Clock } from "lucide-react";
import "./TimerExpiredOverlay.css";

const TIMER_EXTENSION_MINUTES = 5;

const TimerExpiredOverlay = () => {
  const [showTimerExpired, setShowTimerExpired] = useState(false);
  const [setTimeText, setSetTimeText] = useState("-");
  const [durationText, setDurationText] = useState("-");

  const formatSetTime = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}.${minutes}`;
  };

  const formatDuration = (durationMinutes) => {
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) return "-";
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}min`;
  };

  const handleExtendTimer = (event) => {
    event.stopPropagation();
    const setAt = Date.now();
    const endTime = setAt + TIMER_EXTENSION_MINUTES * 60000;

    localStorage.setItem("studyTimer", endTime.toString());
    localStorage.setItem(
      "studyTimerInfo",
      JSON.stringify({
        setAt,
        durationMinutes: TIMER_EXTENSION_MINUTES,
      }),
    );
    window.dispatchEvent(
      new CustomEvent("study-timer-extended", {
        detail: { endTime },
      }),
    );
    setShowTimerExpired(false);
  };

  const checkTimer = useCallback(() => {
    const savedTimer = localStorage.getItem("studyTimer");
    if (!savedTimer) return;

    const endTime = parseInt(savedTimer, 10);
    const now = Date.now();
    if (Number.isNaN(endTime)) {
      localStorage.removeItem("studyTimer");
      localStorage.removeItem("studyTimerInfo");
      return;
    }

    if (now >= endTime) {
      const elapsed = now - endTime;
      if (elapsed < 3600000) {
        const rawTimerInfo = localStorage.getItem("studyTimerInfo");
        if (rawTimerInfo) {
          try {
            const timerInfo = JSON.parse(rawTimerInfo);
            setSetTimeText(formatSetTime(timerInfo.setAt));
            setDurationText(formatDuration(timerInfo.durationMinutes));
          } catch {
            setSetTimeText("-");
            setDurationText("-");
          }
        } else {
          setSetTimeText("-");
          setDurationText("-");
        }
        setShowTimerExpired(true);
      }
      localStorage.removeItem("studyTimer");
      localStorage.removeItem("studyTimerInfo");
    }
  }, []);

  useEffect(() => {
    checkTimer();
    const interval = setInterval(checkTimer, 1000);
    return () => clearInterval(interval);
  }, [checkTimer]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkTimer();
      }
    };

    window.addEventListener("focus", checkTimer);
    window.addEventListener("study-timer-recheck", checkTimer);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", checkTimer);
      window.removeEventListener("study-timer-recheck", checkTimer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [checkTimer]);

  if (!showTimerExpired) return null;

  return (
    <div
      className="timer-expired-overlay"
      onClick={() => setShowTimerExpired(false)}
    >
      <div
        className="timer-expired-card"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="timer-expired-main">
          <Clock size={32} />
          <div className="timer-expired-copy">
            <div className="timer-expired-title">Timer Expired</div>
            <div className="timer-expired-detail">Set time: {setTimeText}</div>
            <div className="timer-expired-detail">Duration: {durationText}</div>
          </div>
        </div>
        <div className="timer-expired-actions">
          <button
            className="timer-expired-button timer-expired-button-primary"
            onClick={handleExtendTimer}
            type="button"
          >
            Extend 5 minutes
          </button>
          <button
            className="timer-expired-button"
            onClick={() => setShowTimerExpired(false)}
            type="button"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimerExpiredOverlay;
